import getS3AssetUrl from 'utils/get-s3-asset-url';
import {
  S3Prefix,
  CharacterCase,
  CharacterType,
  AnimationType,
} from 'types/enums';
import { createAudioAsync } from 'utils/fetch-audio';

class AudioManager {
  letterAudioEls: any[];
  animationAudioEls: any;
  downloadErrorLog: any[];
  _downloadTimeout: any;
  lastAudioVolume: any;
  lastAnimationVolume: any;
  constructor() {
    this.letterAudioEls = [];
    this.animationAudioEls = {};
    this.downloadErrorLog = [];
    this._downloadTimeout = null;
    this.lastAudioVolume = 0.0;
    this.lastAnimationVolume = 0.0;
  }

  fetchAudioList({
    character,
    characterCase,
    characterType,
    language,
    pathAmount,
    volume,
    muted,
  }) {
    const audioPaths = this._getCharacterAudioPaths({
      character,
      characterCase,
      characterType,
      language,
      pathAmount,
    });
    let downloadCounter = 0;

    return new Promise((resolve: any) => {
      this._downloadTimeout = setTimeout(() => {
        // In case the server doesn't respond in a timely manner,
        // we need to manually fill up the audio array with dummy
        // elements
        this.downloadErrorLog.push({
          error: 'CAPTION AUDIO: The download procedure has timed out.',
        });
        audioPaths.forEach((audioPath, index) => {
          if (!this.letterAudioEls[index]) {
            this.letterAudioEls.push({ index });
          }
        });
        this.sortAudioEls();
        resolve();
      }, 10000);

      audioPaths.forEach((filePath, index) => {
        createAudioAsync(getS3AssetUrl(filePath)).then(
          (audioEl: any) => {
            audioEl.index = index;
            if (muted) {
              this.lastAudioVolume = volume;
              audioEl._volume = 0;
            } else {
              audioEl._volume = volume;
            }
            this.letterAudioEls.push(audioEl);
            downloadCounter++;

            if (downloadCounter === audioPaths.length) {
              this.clearDownloadTimeout();
              this.sortAudioEls();
              resolve();
            }
          },
          (error) => {
            this.downloadErrorLog.push({
              character,
              characterCase,
              characterType,
              error,
            });
            this.letterAudioEls.push({
              index,
            });
            downloadCounter++;

            if (downloadCounter === audioPaths.length) {
              this.clearDownloadTimeout();
              this.sortAudioEls();
              resolve();
            }
          }
        );
      });
    });
  }

  toggleMuteAll = (mute) => {
    this.letterAudioEls.forEach((audioEl) => {
      if (mute) {
        this.lastAudioVolume = audioEl.volume
          ? audioEl.volume()
          : audioEl._volume;
      }
      if (audioEl.volume) {
        audioEl.volume(mute ? 0.0 : this.lastAudioVolume);
      } else {
        audioEl._volume = mute ? 0.0 : this.lastAudioVolume;
      }
    }, this);

    for (let animationElKey in this.animationAudioEls) {
      const audioEl = this.animationAudioEls[animationElKey];

      if (mute) {
        this.lastAnimationVolume = audioEl.volume
          ? audioEl.volume()
          : audioEl._volume;
      }
      if (audioEl.volume) {
        audioEl.volume(mute ? 0.0 : this.lastAnimationVolume);
      } else {
        audioEl._volume = mute ? 0.0 : this.lastAnimationVolume;
      }
    }
  };

  setVolume(volume) {
    const newVolume = volume / 100.0;

    this.letterAudioEls.forEach(function (audioEl) {
      try {
        audioEl.volume(newVolume);
      } catch (e) {
        audioEl._volume = newVolume;
      }
    });

    for (let animationElKey in this.animationAudioEls) {
      try {
        this.animationAudioEls[animationElKey].volume(newVolume * 0.2);
      } catch (e) {
        this.animationAudioEls[animationElKey]._volume = newVolume * 0.2;
      }
    }
  }

  sortAudioEls = () => {
    this.letterAudioEls = this.letterAudioEls.sort(function (
      leftAudioEl,
      rightAudioEl
    ) {
      return leftAudioEl.index - rightAudioEl.index;
    });
  };

  getAudioDuration = (audioElIndex) => {
    if (audioElIndex !== -1 && this.letterAudioEls[audioElIndex].duration) {
      return this.letterAudioEls[audioElIndex]._duration;
    }

    return this._getAverageAudioDuration();
  };

  _getAverageAudioDuration() {
    let totalDuration = 0;
    let downloadedAudioCount = 0;

    this.letterAudioEls.forEach(function (audioEl) {
      if (audioEl._duration) {
        totalDuration += audioEl._duration;
        downloadedAudioCount++;
      }
    });

    return totalDuration / downloadedAudioCount;
  }

  playAudio(audioElIndex) {
    if (this.letterAudioEls[audioElIndex]?.play) {
      this.letterAudioEls[audioElIndex].play();
    }
  }

  _getCharacterAudioPaths = ({
    character,
    characterCase,
    characterType,
    language,
    pathAmount,
  }) => {
    const paths: any = [];
    const isNumber = character >= 0 && character < 11;
    // TODO: Remove provisionalCharacterType's usage, when the S3 Bucket naming issues will be solved
    const provisionalCharacterType =
      characterCase === CharacterCase.UPPERCASE ? 'capital' : characterCase;
    let s3Prefix = '';
    let path = isNumber
      ? `letter-formation-audio/${language}/numbers/${character}`
      : `letter-formation-audio/${language}/letters/${characterType}/${provisionalCharacterType}/${this._replaceSpanishN(
          character
        )}`;

    // if (characterCase === CharacterCase.UPPERCASE && characterType === CharacterType.PRINT) {
    //   s3Prefix = S3Prefix.PRINT_UPPERCASE;
    // }
    if (
      provisionalCharacterType === 'capital' &&
      characterType === CharacterType.PRINT
    ) {
      s3Prefix = S3Prefix.PRINT_UPPERCASE;
    }

    if (
      characterCase === CharacterCase.LOWERCASE &&
      characterType === CharacterType.PRINT
    ) {
      s3Prefix = S3Prefix.PRINT_LOWERCASE;
    }

    // if (characterCase === CharacterCase.UPPERCASE && characterType === CharacterType.CURSIVE) {
    //   s3Prefix = S3Prefix.CURSIVE_UPPERCASE;
    // }
    if (
      provisionalCharacterType === 'capital' &&
      characterType === CharacterType.CURSIVE
    ) {
      s3Prefix = S3Prefix.CURSIVE_UPPERCASE;
    }

    if (
      characterCase === CharacterCase.LOWERCASE &&
      characterType === CharacterType.CURSIVE
    ) {
      s3Prefix = S3Prefix.CURSIVE_LOWERCASE;
    }

    // if (characterCase === CharacterCase.LOWERCASE && characterType === CharacterType.PRINT) {
    //   s3Prefix = S3Prefix.PRINT_LOWERCASE;
    // }

    // if (characterCase === CharacterCase.LOWERCASE && characterType === CharacterType.CURSIVE) {
    //   s3Prefix = S3Prefix.CURSIVE_LOWERCASE;
    // }

    for (let i = 0; i < pathAmount; i++) {
      if (isNumber) {
        paths.push(`${path}/${character}_${i}.mp3`);
      } else {
        paths.push(
          `${path}/${this._replaceSpanishN(character)}_${s3Prefix}_${i}.mp3`
        );
      }
    }

    return paths;
  };

  _replaceSpanishN(character) {
    switch (character) {
      case 'Ñ': {
        return 'N_spanish';
      }
      case 'ñ': {
        return 'n_spanish';
      }
      default: {
        return character;
      }
    }
  }

  _getAnimationAudioS3Url(animationType) {
    let url = 'letter-formation-audio/animation-sfx/';

    switch (animationType) {
      case AnimationType.FROG_JUMP: {
        url += 'frog';
        break;
      }

      case AnimationType.DIVE_DOWN:
      case AnimationType.DIVE_DOWN_DEEP: {
        url += 'dive';
        break;
      }

      case AnimationType.HELICOPTER:
      case AnimationType.HIGH_HELICOPTER: {
        url += 'helicopter';
        break;
      }

      case AnimationType.BUMP_INNER:
      case AnimationType.BUMP: {
        url += 'bump';
        break;
      }

      case AnimationType.FIREFIGHTER:
      case AnimationType.SLIDE_DOWN:
      case AnimationType.SLIDE_DOWN_REVERSED:
      case AnimationType.SLIDE_AWAY: {
        url += 'slide';
        break;
      }

      case AnimationType.MAGIC_C: {
        url += 'magic';
        break;
      }

      case AnimationType.SWIM:
      case AnimationType.LONG_SWIM: {
        url += 'swim';
        break;
      }

      case AnimationType.BASEBALL: {
        url += 'baseball';
        break;
      }

      case AnimationType.TOW: {
        url += 'tow';
        break;
      }

      case AnimationType.JET: {
        url += 'jet';
        break;
      }

      case AnimationType.KARATE_KICK: {
        url += 'karate';
        break;
      }

      default: {
        url = '';
      }
    }

    url += '.mp3';
    return url;
  }

  fetchAnimationAudio(animationType, volume, muted) {
    const animationAudioUrl = getS3AssetUrl(
      this._getAnimationAudioS3Url(animationType)
    );
    let downloadTimeout: any = null;

    return new Promise((resolve: any) => {
      downloadTimeout = setTimeout(() => {
        this.downloadErrorLog.push({
          error:
            'ANIMATION AUDIO: The server failed to respond in a timely manner.',
        });
        this.animationAudioEls[animationType] = {
          play() {},
        };
        resolve();
      }, 2000);

      createAudioAsync(animationAudioUrl).then(
        (audioEl: any) => {
          clearTimeout(downloadTimeout);
          if (muted) {
            this.lastAnimationVolume = volume * 0.2;
            audioEl._volume = 0.0;
          } else {
            audioEl._volume = volume * 0.2;
          }
          this.animationAudioEls[animationType] = audioEl;
          resolve();
        },
        (err) => {
          clearTimeout(downloadTimeout);
          this.downloadErrorLog.push(err);
          this.animationAudioEls[animationType] = {
            play() {},
          };
          resolve();
        }
      );
    });
  }

  clearDownloadTimeout() {
    clearTimeout(this._downloadTimeout);
  }
}
const audioManager = new AudioManager();

export default audioManager;
